<template>
    <div class="tw-grid tw-grid-cols-12 tw-space-x-8 tw-items-center">
        <div class="tw-col-span-7">
            <div class="fake-link skill-title tw-cursor-pointer tw-truncate">
                <span
                    tabindex="0"
                    @keyup.enter="showDetail(listItem)"
                    @click="showDetail(listItem)"
                >
                    <strong>
                        {{
                            listItem[groupBy] && listItem[groupBy].name
                                ? listItem[groupBy].name
                                : "All"
                        }}
                    </strong>
                </span>
            </div>

            <div class="tw-flex mouseover-parent">
                <div
                    v-b-popover.hover.top="{
                        content: $t('surveys.details.helpDate'),
                    }"
                    class="color-grey tw-mr-2"
                >
                    {{ listItem.periodStart | moment("MMM D, YYYY") }}
                </div>
                &middot;
                <div
                    v-b-popover.hover.top="{
                        content: $t('surveys.details.helpParticipantCount'),
                    }"
                    class="color-grey tw-ml-2 tw-mr-2"
                >
                    {{ listItem.cnt_distinct_sender }}
                    <span>{{ $t("surveys.details.labelParticipants") }} </span>
                </div>
                &middot;
                <div
                    v-if="listItem.scaleType === 'nps' || !listItem.scaleType"
                    v-b-popover.hover.top="{
                        content: $t('surveys.details.helpNPS'),
                    }"
                    class="color-grey tw-mr-4 tw-ml-2"
                >
                    {{
                        (listItem.percentage_positive - listItem.percentage_negative).toFixed(0)
                    }}&#37;
                </div>

                <div
                    v-if="isOwner && listItem.question && listItem.question.impact != null"
                    class="color-grey text-center tw-mr-2"
                >
                    <i
                        v-b-popover.hover.top="{
                            content: $t('surveys.details.helpImpact'),
                        }"
                        class="tw-mr-2"
                        :class="{
                            'fal fa-bullseye': listItem.question.impact === -1,
                            'fas fa-circle color-success': listItem.question.impact === 5,
                            'fas fa-circle color-success tw-opacity-80':
                                listItem.question.impact === 4,
                            'fas fa-circle color-success tw-opacity-60':
                                listItem.question.impact === 3,
                            'fas fa-circle color-success tw-opacity-40':
                                listItem.question.impact === 2,
                            'fas fa-circle color-success tw-opacity-20':
                                listItem.question.impact === 1,
                            'fas fa-circle tw-color-gray-100':
                                listItem.question.impact === 0 ||
                                typeof listItem.question.impact === 'undefined',
                        }"
                    >
                    </i>

                    <span
                        v-b-popover.hover="{
                            content: $t(
                                'surveys.details.helpImpactLevel.' + listItem.question.impact,
                            ),
                        }"
                    >
                        {{ $t(`surveys.details.labelImpactLevel.${listItem.question.impact}`) }}
                    </span>
                </div>

                <div
                    class="fake-link clickable mouseover tw-ml-2"
                    tabindex="0"
                    @keyup.enter="showDetail(listItem)"
                    @click="showDetail(listItem)"
                >
                    <i class="fal fa-chart-line tw-mr-2"></i>
                    <span>{{ $t("surveys.details.labelShowTimeline") }} </span>
                </div>
            </div>
        </div>

        <div v-if="listItem.avg != 'none'" class="tw-col-span-3">
            <div
                v-if="listItem.scaleType === 'nps' || !listItem.scaleType"
                class="progress progress-success-bg"
                style="margin-top: 20px"
            >
                <div
                    v-show="listItem.percentage_negative > 0"
                    v-b-popover.hover.top="{
                        content: $t('surveys.details.labelDetractor', {
                            percentage: listItem.percentage_negative,
                        }),
                    }"
                    class="progress-bar progress-bar-danger"
                    role="progressbar"
                    :style="`width:${listItem.percentage_negative}%`"
                ></div>
                <div
                    v-show="listItem.percentage_neutral > 0"
                    v-b-popover.hover.top="{
                        content: $t('surveys.details.labelNeutral', {
                            percentage: listItem.percentage_neutral,
                        }),
                    }"
                    class="progress-bar progress-bar-grey"
                    role="progressbar"
                    :style="`width:${listItem.percentage_neutral}%`"
                ></div>
                <div
                    v-show="listItem.percentage_positive > 0"
                    v-b-popover.hover.top="{
                        content: $t('surveys.details.labelPromoter', {
                            percentage: listItem.percentage_positive,
                        }),
                    }"
                    class="progress-bar progress-bar-success"
                    role="progressbar"
                    :style="
                        `width:${98 - listItem.percentage_neutral - listItem.percentage_negative}%`
                    "
                ></div>
            </div>
        </div>

        <div v-if="listItem.avg_amt !== undefined" class="tw-col-span-2 tw-truncate">
            <span>
                <span class="survey-score"> {{ listItem.avg_amt }} </span>
                /{{ listItem.scaleType === "nps" || !listItem.scaleType ? 10 : 5 }}

                <span v-show="listItem.change > 0" class="explanation color-success">
                    <i style="padding: 0 10px;" class="fal fa-chart-line"></i>
                    <span>
                        {{
                            $t("surveys.details.labelScoreUp", {
                                change: listItem.change,
                            })
                        }}
                    </span>
                </span>
                <span v-show="listItem.change < 0" class="explanation color-warning">
                    <i style="padding: 0 10px;" class="fal fa-chart-line-down"></i>
                    <span
                        >{{
                            $t("surveys.details.labelScoreDown", {
                                change: listItem.change * -1,
                            })
                        }}
                    </span>
                </span>
                <br />
                <span v-if="canSeeBenchmark">
                    <span
                        v-if="
                            listItem[groupBy] &&
                                listItem[groupBy].surveyBenchmark &&
                                listItem[groupBy].surveyBenchmark[
                                    listItem.scaleType === 'nps' || !listItem.scaleType
                                        ? 'nps'
                                        : 'likert'
                                ]
                        "
                        v-b-popover.hover="{
                            content: $t('surveys.details.labelBenchmarkHelp'),
                        }"
                        class="explanation color-grey"
                    >
                        <span>{{ $t("surveys.details.labelBenchmark") }} </span>:
                        {{
                            listItem[groupBy].surveyBenchmark[
                                listItem.scaleType === "nps" || !listItem.scaleType
                                    ? "nps"
                                    : "likert"
                            ].avg
                        }}
                    </span>
                    <span v-else class="explanation color-grey">
                        <span>{{ $t("surveys.details.labelNoBenchmark") }} </span>
                    </span>
                </span>
            </span>
        </div>
    </div>
</template>
<script>
import { mapGetters } from "vuex";

export default {
    components: {},
    props: {
        listItem: {
            type: Object,
            required: true,
        },
        groupBy: {
            type: String,
            default: "",
        },
        isOwner: {
            type: Boolean,
            default: false,
        },
        canSeeBenchmark: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {};
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",

            questionList: "surveys/surveys/questionListResult",
            categoryList: "surveys/surveys/categoryListResult",

            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",

            teamSettings: "users/current/teamSettings",
            currentUser: "users/current/user",
        }),
        companyName() {
            return this.teamSettings.name;
        },
    },

    methods: {
        showDetail() {
            this.$emit("showDetail");
        },
    },
};
</script>
